import React from "react";
import type { ScheduleAuditLog } from "../../../../types";
import {
    formatDisplayValue,
    formatShiftDateTime,
    getChangeRows,
    getChanges,
    isColorField,
    isHexColor,
    parseJsonLike,
    toReadableLabel,
} from "./auditUtils";

const Value: React.FC<{ field: string; value: unknown; className?: string }> = ({ field, value, className = "" }) => {
    const text = formatDisplayValue(value);
    if (isColorField(field) && isHexColor(text)) {
        return (
            <span className={`inline-flex items-center gap-1.5 ${className}`}>
                <span className="h-3 w-3 rounded border border-gray-300" style={{ backgroundColor: text }} aria-hidden="true" />
                {text}
            </span>
        );
    }
    return <span className={`break-words ${className}`}>{text}</span>;
};

/** `publish_request` rows: what the manager asked for and what it matched. */
const PublishRequestDetails: React.FC<{ log: ScheduleAuditLog }> = ({ log }) => {
    const changes = getChanges(log) as Record<string, unknown>;
    const request = (parseJsonLike(changes.request) || {}) as Record<string, unknown>;
    const result = (parseJsonLike(changes.result) || {}) as Record<string, unknown>;

    const items: Array<[string, string]> = [];
    if (request.start_date || request.end_date) {
        items.push(["Range", `${formatShiftDateTime(String(request.start_date || ""))} – ${formatShiftDateTime(String(request.end_date || ""))}`]);
    }
    if (request.scope) items.push(["Scope", toReadableLabel(String(request.scope).toLowerCase())]);
    if (request.note) items.push(["Note", String(request.note)]);

    const noMatch = result.no_match === true;
    if (noMatch) items.push(["Result", "No schedules matched"]);
    else if (result.total_schedules != null) {
        items.push(["Result", `${result.total_schedules} schedules · ${result.total_employees ?? 0} employees`]);
    }

    if (items.length === 0) return null;
    return (
        <dl className="mt-2 space-y-1 text-xs">
            {items.map(([label, value]) => (
                <div key={label} className="flex gap-2">
                    <dt className="w-12 flex-shrink-0 text-gray-500">{label}</dt>
                    <dd className={`min-w-0 break-words ${label === "Result" && noMatch ? "text-amber-700" : "text-gray-800"}`}>{value}</dd>
                </div>
            ))}
        </dl>
    );
};

/** Compact "Label  old → new" rows; snapshots (create/copy/delete) render as "Label  value". */
export const AuditChanges: React.FC<{ log: ScheduleAuditLog }> = ({ log }) => {
    if (log.action === "publish_request") return <PublishRequestDetails log={log} />;

    const rows = getChangeRows(log);
    if (rows.length === 0) return null;

    return (
        <div className="mt-2 space-y-1 rounded-md bg-gray-50 px-2.5 py-2 text-xs">
            {rows.map((row) => (
                <div key={row.field} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="w-24 flex-shrink-0 text-gray-500">{row.label}</span>
                    {row.isDiff ? (
                        <span className="min-w-0 flex-1">
                            <Value field={row.field} value={row.old} className="text-gray-500 line-through decoration-gray-300" />
                            <span className="mx-1.5 text-gray-400">→</span>
                            <Value field={row.field} value={row.new} className="font-medium text-gray-900" />
                        </span>
                    ) : (
                        <Value field={row.field} value={row.new} className="min-w-0 flex-1 text-gray-800" />
                    )}
                </div>
            ))}
        </div>
    );
};

export default AuditChanges;
